<!-- src/views/public/HomeDesktop.vue -->
<template>
  <q-page class="home-desktop">
    <!-- Modern Banner Section: 65:35 layout -->
    <div class="modern-banner-section">
      <div class="q-container q-pa-md">
        <div class="row q-col-gutter-md">
          <!-- Main Auto-Sliding Banner (65%) -->
          <div class="col-lg-8 col-md-7 col-sm-12">
            <BannerCarousel
              :banners="mainBanners"
              :loading="bannersLoading"
              :error="bannersError"
              type="main"
              @banner-click="handleBannerClick"
              @img-error="onImgError"
            />
          </div>
          <!-- Side Banner Ad (35%) -->
          <div class="col-lg-4 col-md-5 col-sm-12">
            <BannerCarousel
              :banners="sideBanner"
              :loading="bannersLoading"
              :error="bannersError"
              type="side"
              @banner-click="handleBannerClick"
              @img-error="onImgError"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="q-container q-pa-md">
      <!-- Quasar Tabs Section -->
      <div class="q-mt-lg">
        <q-tabs
          v-model="activeTab"
          dense
          align="left"
          indicator-color="primary"
          class="q-mb-md"
        >
          <q-tab name="featured" label="Featured Deals" icon="star" />
          <q-tab name="stores" label="Popular Stores" icon="store" />
          <q-tab name="coupons" label="Latest Coupons" icon="local_offer" />
        </q-tabs>
        <q-tab-panels v-model="activeTab" animated>
          <q-tab-panel name="featured">
            <FeaturedDeals
              :deals="featuredDeals"
              :loading="loading"
              :error="error"
              @refresh="$emit('refresh')"
              @open-coupon-link="openCouponLink"
              @img-error="onImgError"
              @format-expiry="formatExpiry"
            />
          </q-tab-panel>
          <q-tab-panel name="stores">
            <PopularStores
              :stores="popularStores"
              :loading="loading"
              :error="error"
              @refresh="$emit('refresh')"
              @navigate-to-store="navigateToStore"
              @img-error="onImgError"
            />
          </q-tab-panel>
          <q-tab-panel name="coupons">
            <LatestCoupons
              :coupons="latestCoupons"
              :loading="loading"
              :error="error"
              @refresh="$emit('refresh')"
              @open-coupon-link="openCouponLink"
              @img-error="onImgError"
              @format-expiry="formatExpiry"
            />
          </q-tab-panel>
        </q-tab-panels>
      </div>
      <!-- Categories Grid -->
      <CategoriesGrid
        :categories="categories"
        :loading="loading"
        :error="error"
        @refresh="$emit('refresh')"
      />
      <!-- Newsletter Signup -->
      <NewsletterSignup
        v-model:email="emailModel"
        @submit="handleNewsletter"
        :validate-email="validateEmail"
      />
    </div>
    
    <!-- Coupon Detail Dialog -->
    <CouponDetailDialog
      v-if="selectedCoupon"
      v-model="showCouponDialog"
      :coupon="selectedCoupon"
    />
  </q-page>
</template>

<script setup>
/**
 * HomeDesktop.vue - Desktop optimized home page
 * 
 * Features:
 * - Spacious layouts with premium feel for larger screens
 * - Visual hierarchy optimized for desktop viewing
 * - Grid-based layouts to utilize screen real estate efficiently
 * - Image and content optimizations for desktop performance
 */
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { formatDate } from '@/utils/dateUtils';

import BannerCarousel from 'src/components/public/BannerCarousel.vue';
import FeaturedDeals from 'src/components/public/FeaturedDeals.vue';
import PopularStores from 'src/components/public/PopularStores.vue';
import CategoriesGrid from 'src/components/public/CategoriesGrid.vue';
import LatestCoupons from 'src/components/public/LatestCoupons.vue';
import NewsletterSignup from 'src/components/public/NewsletterSignup.vue';
import CouponDetailDialog from 'src/components/coupon/CouponDetailDialog.vue';

const $q = useQuasar();
const router = useRouter();

// Dialog state for coupon details
const showCouponDialog = ref(false);
const selectedCoupon = ref(null);
const activeTab = ref('featured');

// Props and emits
const props = defineProps({
  featuredStores: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  featuredCoupons: { type: Array, default: () => [] },
  latestCoupons: { type: Array, default: () => [] },
  mainBanners: { type: Array, default: () => [] },
  sideBanner: { type: Array, default: () => [] },
  loading: Boolean,
  bannersLoading: Boolean,
  bannersError: String,  error: String,
  searchQuery: String,
  email: String
});

const emit = defineEmits(['update:searchQuery', 'update:email', 'subscribe', 'refresh']);

// Map props to UI data for clear usage in template
const featuredDeals = computed(() => props.featuredCoupons || []);
const popularStores = computed(() => props.featuredStores || []);
const categories = computed(() => props.categories || []);
const latestCoupons = computed(() => props.latestCoupons || []);

// Banner handling
const currentMainSlide = ref(0);
const currentSideSlide = ref(0);
const mainSliderFallbackSlide = ref('slide1');
const autoplayStopped = ref(false);

// Pause and resume autoplay for sliders
const pauseAutoplay = () => {
  autoplayStopped.value = true;
  // Find and stop all carousel autoplay timers
  const carousels = document.querySelectorAll('.q-carousel');
  carousels.forEach(carousel => {
    const vm = carousel.__vue__;
    if (vm && vm.stopTimer) {
      vm.stopTimer();
      console.log('Paused autoplay timer for carousel');
    } else {
      console.log('Could not access carousel Vue instance or stopTimer method');
    }
  });
};

const resumeAutoplay = () => {
  autoplayStopped.value = false;
  // Find and restart all carousel autoplay timers
  const carousels = document.querySelectorAll('.q-carousel');
  console.log(`Found ${carousels.length} carousels to restart`);
  carousels.forEach(carousel => {
    const vm = carousel.__vue__;
    if (vm && vm.startTimer) {
      vm.startTimer();
      console.log('Started autoplay timer for carousel');
    } else {
      console.log('Could not access carousel Vue instance or startTimer method');
      // Alternative approach using autoplay prop
      if (carousel.__vue__) {
        carousel.__vue__.injectProp('autoplay', true);
        console.log('Tried alternative approach to restart autoplay');
      }
    }
  });
};

// Handle banner click with tracking
const handleBannerClick = (banner) => {
  if (!banner || !banner.link_url) return;
  
  // Import required to avoid circular dependencies
  import("@/services/supabase").then(({ bannerService }) => {
    // Track the click for analytics
    bannerService.trackClick(banner.id);
    
    // Open the link in a new tab
    window.open(banner.link_url, '_blank');
  }).catch(err => {
    console.error("Failed to track banner click:", err);
    // Still open the link even if tracking fails
    window.open(banner.link_url, '_blank');  });
};

// Two-way binding for email and search
const searchQueryModel = computed({
  get: () => props.searchQuery || '',
  set: val => emit('update:searchQuery', val)
});

const emailModel = computed({
  get: () => props.email || '',
  set: val => emit('update:email', val)
});  

// Banner handling
onMounted(() => {
    // Initialize the carousel slides and start autoplay after a short delay
    setTimeout(() => {
      if (props.mainBanners && props.mainBanners.length > 0) {
        currentMainSlide.value = 0;
      }
        if (props.sideBanner && props.sideBanner.length > 0) {
        currentSideSlide.value = 0;
      }
      
      // Start the autoplay explicitly
      resumeAutoplay();
    }, 500);
  });
const getStoreDeals = () => {
  if (!props.featuredCoupons || !props.featuredStores) return [];
  
  // Use isFromPopularStore flag to directly filter coupons from popular stores
  const storeDeals = props.featuredCoupons.filter(
    coupon => coupon.isFromPopularStore === true
  );
  
  console.log(`Found ${storeDeals.length} coupons with isFromPopularStore flag`);
  
  // If we don't have enough coupons with the flag, fallback to using store_id
    // If we don't have enough coupons with the flag, fallback to using store_id
  if (storeDeals.length < 3) {
    // Fallback to using store_id
    const popularStoreIds = props.featuredStores.map(store => store.id);
    
    // Check the latestCoupons for additional coupons from popular stores
    if (props.latestCoupons) {
      const additionalDeals = props.latestCoupons.filter(
        coupon => popularStoreIds.includes(coupon.store_id) && 
        !storeDeals.some(deal => deal.id === coupon.id)
      );
      
      console.log(`Found ${additionalDeals.length} additional deals from popular stores in latestCoupons`);
      
      // Combine and limit to 4 deals total
      return [...storeDeals, ...additionalDeals].slice(0, 4);
    }
  }
  
  return storeDeals.slice(0, 4);
};

/**
 * Handles search form submission
 * Redirects to search page with query parameter
 */
const handleSearch = () => {
  if (searchQueryModel.value?.trim()) {
    router.push({
      path: '/search',
      query: { q: searchQueryModel.value }
    });
  }
};

/**
 * Handles newsletter subscription form submission
 */
const handleNewsletter = () => {
  if (validateEmail(emailModel.value)) {
    emit('subscribe');
  }
};

/**
 * Copies coupon code to clipboard and shows notification
 */
const copyCouponCode = (code) => {
  navigator.clipboard.writeText(code)
    .then(() => {
      $q.notify({
        message: 'Code copied to clipboard',
        color: 'positive',
        position: 'top',
        timeout: 2000
      });
    })
    .catch(() => {
      $q.notify({
        message: 'Failed to copy code',
        color: 'negative',
        position: 'top',
        timeout: 2000
      });
    });
};

/**
 * Add a method for opening coupon links via the redirect endpoint
 * First shows the dialog, then opens the redirect URL
 */
const openCouponLink = (coupon) => {
  // Set the selected coupon and show the dialog
  selectedCoupon.value = coupon;
  showCouponDialog.value = true;
  
  // Delay opening the URL slightly to ensure dialog appears first
  setTimeout(() => {
    window.open(`https://autoredirect.CouponLa-india.workers.dev/${coupon.id}`, '_blank');
  }, 100);
};

/**
 * Navigate to store page using router
 */
const navigateToStore = (slug) => {
  if (!slug) return;
  
  router.push({
    name: 'Store',
    params: { slug: slug }
  });
};

/**
 * Format expiry date to a friendly format
 */
const formatExpiry = (expireDate) => {
  if (!expireDate) return 'No expiration';
  
  const expiry = new Date(expireDate);
  const today = new Date();
  const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return 'Expired';
  if (diffDays === 0) return 'Expires today';
  if (diffDays === 1) return 'Expires tomorrow';
  if (diffDays < 30) return `Expires in ${diffDays} days`;
  
  return formatDate(expireDate, 'MMM D, YYYY');
};

/**
 * Simple email validation
 */
const validateEmail = (email) => {
  return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Handle image loading errors
 */
const onImgError = (err) => {
  // Set default placeholder image
  err.target.src = '/images/placeholder.png';
};

/**
 * Optimize image URLs to reduce size and improve loading
 * - Adds compression parameters for supported image services
 * - Ensures appropriate size based on device
 */
const optimizeImageUrl = (url) => {
  if (!url) return '/images/placeholder.png';
  
  try {
    // Check if it's a Supabase Storage URL
    if (url.includes('supabase') && url.includes('storage')) {
      // Add width parameter to resize image on the server side
      const width = $q.screen.lt.sm ? 500 : ($q.screen.lt.md ? 800 : 1000);
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}width=${width}&quality=80`;
    }
    
    // Handle other image services (example for Cloudinary, Imgix, etc.)
    if (url.includes('cloudinary')) {
      return url.replace('/upload/', '/upload/w_1000,q_auto,f_auto/');
    }
    
    // Return original URL if no optimization is possible
    return url;
  } catch (err) {
    console.error('Error optimizing image URL:', err);
    return url; // Return original URL in case of error
  }
};

// Add dialog state declarations here if they weren't already defined
</script>

<style lang="scss" scoped>

.promo-banner-content {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  position: relative;
}

.promo-banner-text {
  font-weight: 500;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
}

.promo-shop-btn {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  color: white;
  padding: 0 12px;
  height: 24px;
  min-height: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.2s ease;
}

.promo-shop-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.promo-close-btn {
  opacity: 0.7;
  font-size: 1rem;
  margin-left: 8px;
  transition: opacity 0.2s;
}

.promo-close-btn:hover {
  opacity: 1;
}

.q-container {
  max-width: 1280px;
  margin: 0 auto;
}

.home-desktop {
  min-height: 100%;
  background-color: #f5f7fa;
}

.hero-banner {
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 200%;
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(-12deg);
    z-index: 1;
  }
  
  .q-container {
    position: relative;
    z-index: 2;
  }
}

.hero-image {
  max-width: 90%;
  filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.15));
  animation: float 6s ease-in-out infinite;
}

/* Modern Banner Section Styles - 2025 Design */
.modern-banner-section {
  padding-top: 1.5rem;
  background-color: #f8f9fa;
}

.main-banner-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0,0,0,0.13), 0 1.5px 8px rgba(255,152,0,0.08);
  background: linear-gradient(135deg, #fff 80%, #fffaf2 100%);
  border: 1.5px solid #ffe0b2;
  overflow: hidden;
  position: relative;
  height: 240px;
  min-height: 240px;
  max-height: 240px;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
}

.side-banner-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 35px rgba(0,0,0,0.1), 0 1.5px 8px rgba(33,150,243,0.07);
  background: linear-gradient(135deg, #fff 80%, #f5f9ff 100%);
  border: 1.5px solid #bbdefb;
  overflow: hidden;
  position: relative;
  height: 240px;
  min-height: 240px;
  max-height: 240px;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
}

.main-banner-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: 12px;
  background: linear-gradient(120deg, rgba(255,152,0,0.08) 0%, rgba(255,87,34,0.06) 100%);
  z-index: 1;
}

.side-banner-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: 16px;
  background: linear-gradient(120deg, rgba(33,150,243,0.06) 0%, rgba(3,169,244,0.04) 100%);
  z-index: 1;
}

.main-banner-card:hover {
  transform: translateY(-8px) scale(1.01);
  box-shadow: 0 25px 65px rgba(255,152,0,0.18), 0 8px 35px rgba(0,0,0,0.18);
  border-color: #ff9800;
  background: linear-gradient(135deg, #fffbe6 70%, #fff3e0 100%);
}

.side-banner-card:hover {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 20px 50px rgba(33,150,243,0.15), 0 8px 25px rgba(0,0,0,0.12);
  border-color: #2196f3;
  background: linear-gradient(135deg, #f5f9ff 70%, #e3f2fd 100%);
}

.main-carousel-slide {
  overflow: hidden;
  border-radius: 12px;
}

.main-banner-img {
  border-radius: 0 0 12px 12px;
  min-height: 240px;
  max-height: 240px;
  height: 100%;
  object-fit: contain !important;
  width: 100%;
  transition: transform 0.6s cubic-bezier(.4,2,.3,1), box-shadow 0.3s;
  box-shadow: 0 2px 12px rgba(255,152,0,0.07);
  filter: brightness(0.98) contrast(1.04) saturate(1.08) drop-shadow(0 4px 18px rgba(255,152,0,0.08));
  display: block;
  background: #fff;
}

.side-banner-img {
  border-radius: 0 0 16px 16px;
  min-height: 240px;
  max-height: 240px;
  height: 100%;
  object-fit: contain !important;
  width: 100%;
  transition: transform 0.6s cubic-bezier(.4,2,.3,1), box-shadow 0.3s;
  box-shadow: 0 2px 10px rgba(33,150,243,0.06);
  filter: brightness(0.99) contrast(1.02) saturate(1.04) drop-shadow(0 4px 15px rgba(33,150,243,0.07));
  display: block;
}

.banner-content {
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
  padding: 0.5rem 1.2rem;
  background: none;
  pointer-events: none;
}

.banner-content-inner {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  background: none;
  box-shadow: none;
  padding: 0;
  min-width: unset;
  max-width: 15vw;
  width: auto;
  pointer-events: auto;
}

.banner-gradient-bar {
  width: 6px;
  min-width: 6px;
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(180deg, #ff9800 0%, #ff5722 100%);
  margin-right: 0.7rem;
  margin-top: 0.1rem;
  margin-bottom: 0.1rem;
}

.banner-content-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.12rem;
}

.banner-content h3,
.banner-content p {
  background: none;
  box-shadow: none;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0,0,0,0.55);
  margin-bottom: 0.12rem;
  padding: 0;
  border-radius: 0;
  font-weight: 600;
  max-width: 15vw;
  white-space: normal;
  word-break: break-word;
  display: block;
}

.banner-content h3 {
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.08rem;
  line-height: 1.2;
}

.banner-content p {
  font-size: 0.9rem;
  font-weight: 400;
  margin-bottom: 0.18rem;
  max-width: 100%;
  line-height: 1.3;
}

.banner-btn {
  border-radius: 16px;
  padding: 0.12rem 0.9rem;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: transparent;
  color: #fff !important;
  border: 2px solid #ff9800;
  box-shadow: none;
  transition: border 0.3s, color 0.3s, transform 0.2s;
  margin-top: 0.1rem;
  margin-left: 0.1rem;
  pointer-events: auto;
}

.banner-btn:hover {
  border-color: #ff5722;
  color: #ff9800 !important;
  background: rgba(255,152,0,0.08);
  transform: translateY(-1px) scale(1.03);
}

.banner-skeleton {
  border-radius: 16px;
  overflow: hidden;
}

.banner-error {
  background-color: rgba(244, 67, 54, 0.05);
  color: #f44336;
  border-radius: 12px;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-banners-placeholder {
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  height: 240px;
}

.banner-skeleton .q-skeleton {
  height: 240px !important;
}

.search-section {
  margin-top: -40px;
  position: relative;
  z-index: 5;
}

.search-input {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  
  :deep(.q-field__control) {
    border-radius: 8px;
    height: 48px;
  }
}

.search-button {
  height: 48px;
  border-radius: 8px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.quick-actions {
  margin-top: 1rem;
  text-align: center;
}

@keyframes float {
  0% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-12px) rotate(1deg); }
  50% { transform: translateY(0px) rotate(0deg); }
  75% { transform: translateY(8px) rotate(-1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
}

.search-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.deal-card,
.store-card,
.coupon-card,
.category-card {
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }
}

.featured-deal-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  transition: all 0.3s ease;
  border: none !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
    
    .deal-image {
      transform: scale(1.05);
    }
    
    .deal-action-btn {
      animation: pulse 1.5s infinite;
    }
  }
  
  .deal-highlight-bar {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 4px;
    z-index: 1;
    
    &.deal-code {
      background: linear-gradient(to bottom, #ff9800, #ff5722);
    }
    
    &.deal-offer {
      background: linear-gradient(to bottom, #4caf50, #2196f3);
    }
  }
  
  .deal-image {
    transition: transform 0.6s ease;
  }
  
  .deal-content {
    padding-bottom: 6px;
  }
  
  .store-name {
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .deal-title {
    font-weight: 600;
    margin-top: 4px;
    color: #263238;
  }
  
  .deal-description {
    color: #607d8b;
  }
  
  .deal-info {
    padding-top: 0;
    padding-bottom: 8px;
    
    .expiry-info {
      color: #ff5722;
      font-weight: 500;
    }
  }
  
  .store-logo-avatar {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    border: 2px solid #fff;
  }
}

.featured-discount-badge {
  font-size: 1rem;
  font-weight: 600;
  padding: 4px 12px;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.featured-heading {
  position: relative;
  display: inline-block;
}

.heading-underline {
  width: 60px;
  height: 3px;
  background: var(--q-primary);
  margin-top: 8px;
  border-radius: 2px;
}

.view-all-btn {
  font-weight: 500;
  padding-right: 8px;
  
  &:hover {
    text-decoration: underline;
  }
}

.featured-skeleton {
  border-radius: 12px;
}

.error-container {
  background: rgba(244, 67, 54, 0.05);
  border: 1px solid rgba(244, 67, 54, 0.2);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--q-primary), 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(var(--q-primary), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--q-primary), 0);
  }
}

.category-card {
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  min-height: 120px;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 200%;
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(-12deg);
  }
}

.coupon-code-section {
  border-top: 1px dashed #ddd;
  
  .code-display {
    font-family: monospace;
    letter-spacing: 1px;
    background: #fff;
    padding: 3px 8px;
    border-radius: 4px;
    border: 1px solid #ddd;
  }
}

// Accessibility enhancements
.ellipsis-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.discount-badge {
  font-size: 1rem;
  padding: 4px 12px;
}

// Image optimizations
:deep(.q-img) {
  background-color: transparent;
  
  &.main-banner-img {
    transition: transform 0.6s ease;
    transform-origin: center center;
    
    &:hover {
      transform: scale(1.04);
    }
  }
  
  &.side-banner-img {
    transition: transform 0.5s ease;
    transform-origin: center center;
    
    &:hover {
      transform: scale(1.02) translateX(-3px);
    }
  }
}

:deep(.q-img__image) {
  object-fit: cover !important;
  width: 100%;
  height: 100%;
  background: #fff;
}

:deep(.hero-image .q-img__image) {
  background: transparent;
  object-fit: contain;
}

/* Ensure main and side banners are always the same height and aligned */
.main-slider,
.main-carousel-slide .q-img,
.side-slider,
.side-carousel-slide .q-img,
.main-banner-img,
.side-banner-img,
.no-banners-placeholder,
.banner-skeleton .q-skeleton {
  height: 240px !important;
  min-height: 240px !important;
  max-height: 240px !important;
}

.col-lg-8.col-md-7.col-sm-12,
.col-lg-4.col-md-5.col-sm-12 {
  display: flex;
  flex-direction: column;
  justify-content: stretch;
}

@media (max-width: 1200px) {
  .main-slider,
  .main-carousel-slide .q-img,
  .side-slider,
  .side-carousel-slide .q-img,
  .main-banner-img,
  .side-banner-img,
  .no-banners-placeholder,
  .banner-skeleton .q-skeleton {    height: 240px !important;
    min-height: 240px !important;
    max-height: 240px !important;
  }
}

/* Responsive adjustments for featured deal cards */
@media (max-width: 1440px) {
  .featured-deal-card .deal-image {
    height: 150px;
  }
}

@media (max-width: 1200px) {
  .featured-deal-card .deal-title {
    font-size: 1.1rem;
  }
  .featured-deal-card .deal-description {
    font-size: 0.85rem;
  }
  /* Banner height handled by the dedicated banner section above */  .main-banner-card,
  .side-banner-card {
    min-height: 240px;
    max-height: 240px;
    height: 240px;
  }
  .banner-content {
    max-width: 80%;
    padding-left: 0.7rem;
    padding-right: 0.7rem;
    padding-bottom: 0.7rem;
  }
  .banner-content-inner {
    max-width: 22vw;
    min-width: 120px;
    padding: 0.4rem 0.7rem;
  }
  .banner-content h3 {
    font-size: 0.95rem;
  }
  .banner-content p {
    font-size: 0.85rem;
  }
  .banner-btn {
    font-size: 0.85rem;
    padding: 0.15rem 0.7rem;
  }
}

@media (max-width: 992px) {
  .featured-deal-card .deal-image {
    height: 140px;
  }
  .featured-discount-badge {
    font-size: 0.9rem;
    padding: 3px 10px;
  }
}

@media (max-width: 767px) {
  .row.q-col-gutter-lg {
    margin-left: -12px;
    margin-right: -12px;
  }
  .row.q-col-gutter-lg > div {
    padding-left: 12px;
    padding-right: 12px;
  }
  
  /* Mobile-specific banner heights */
  .main-slider, 
  .main-carousel-slide .q-img, 
  .side-slider,
  .side-carousel-slide .q-img,
  .main-banner-img,
  .side-banner-img,
  .no-banners-placeholder,
  .banner-skeleton .q-skeleton {
    height: 220px !important;
    min-height: 220px !important;
    max-height: 220px !important;
  }
  .banner-content {
    padding-left: 0.7rem;
    padding-right: 0.7rem;
    padding-bottom: 0.7rem;
  }
  .banner-content-inner {
    max-width: 40vw;
    min-width: 80px;
    padding: 0.3rem 0.5rem;
  }
  .banner-content h3 {
    font-size: 0.85rem;
  }
  .banner-content p {
    font-size: 0.78rem;
    max-width: 100%;
  }
  .banner-btn {
    font-size: 0.75rem;
    padding: 0.1rem 0.5rem;
  }
}

.banner-accent-bar {
  width: 5px;
  min-width: 5px;
  height: 60px;
  background: linear-gradient(180deg, #ff9800 0%, #ff5722 100%);
  border-radius: 4px;
  margin-right: 1.1rem;
  margin-bottom: 0.2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.18);
  align-self: flex-end;
}

.banner-content-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 15vw;
  min-width: 120px;
  pointer-events: auto;
}

.banner-content-text h3,
.banner-content-text p {
  color: #fff;
  text-shadow: 0 2px 8px rgba(0,0,0,0.45);
  margin-bottom: 0.15rem;
  background: none;
  border-radius: 0;
  padding: 0.1rem 0;
  display: inline-block;
  font-weight: 600;
  max-width: 100%;
}

.banner-content-text h3 {
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.08rem;
  line-height: 1.2;
}

.banner-content-text p {
  font-size: 0.9rem;
  font-weight: 400;
  margin-bottom: 0.18rem;
  line-height: 1.3;
}

.banner-btn.outline {
  border-radius: 16px;
  padding: 0.12rem 0.9rem;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: transparent !important;
  color: #ff9800 !important;
  border: 2px solid #ff9800 !important;
  box-shadow: none;
  margin-top: 0.1rem;
  margin-left: 0.1rem;
  pointer-events: auto;
  transition: border 0.2s, color 0.2s, background 0.2s;
}

.banner-btn.outline:hover {
  color: #fff !important;
  background: linear-gradient(90deg, #ff9800 0%, #ff5722 100%) !important;
  border-color: #ff5722 !important;
}

/* --- Banner Glassmorphism Refactor --- */
.banner-glass-content {
  position: absolute;
  left: 2.5vw;
  bottom: 2.5vw;
  z-index: 3;
  display: flex;
  align-items: flex-end;
  min-width: 180px;
  max-width: 18vw;
  background: linear-gradient(120deg, rgba(255,255,255,0.18) 60%, rgba(255,255,255,0.08) 100%);
  border-radius: 1.5rem;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  border: 1.5px solid rgba(255,255,255,0.25);
  backdrop-filter: blur(14px) saturate(1.2);
  -webkit-backdrop-filter: blur(14px) saturate(1.2);
  padding: 1.1rem 1.7rem 1.1rem 1.3rem;
  pointer-events: auto;
  transition: box-shadow 0.2s, border 0.2s, opacity 0.2s, visibility 0.2s;
  opacity: 0;
  visibility: hidden;
}

// Show glass content on hover or focus of banner image
:deep(.main-banner-img:hover) .banner-glass-content,
:deep(.main-banner-img:focus) .banner-glass-content,
:deep(.side-banner-img:hover) .banner-glass-content,
:deep(.side-banner-img:focus) .banner-glass-content {
  opacity: 1;
  visibility: visible;
}

// Optionally, show on hover of the glass content itself (so it doesn't disappear when moving mouse to button)
.banner-glass-content:hover,
.banner-glass-content:focus-within {
  opacity: 1;
  visibility: visible;
}

.banner-glass-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.19rem;
  width: 100%;
}

.banner-title {
  color: #222;
  font-size: 1.15rem;
  font-weight: 800;
  margin-bottom: 0.18rem;
  letter-spacing: 0.01em;
  text-shadow: 0 2px 12px rgba(255,255,255,0.18), 0 1px 8px rgba(0,0,0,0.10);
  line-height: 1.18;
  max-width: 100%;
  white-space: normal;
}

.banner-desc {
  color: #333;
  font-size: 0.98rem;
  font-weight: 400;
  margin-bottom: 0.19rem;
  text-shadow: 0 2px 8px rgba(255,255,255,0.12), 0 1px 6px rgba(0,0,0,0.08);
  line-height: 1.32;
  max-width: 100%;
  white-space: normal;
}

.banner-glass-btn {
  border-radius: 999px;
  padding: 0.19rem 1.1rem;
  font-size: 0.98rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  background: rgba(255,255,255,0.12) !important;
  color: #ff9800 !important;
  border: 2px solid #ff9800 !important;
  box-shadow: 0 2px 8px rgba(255,152,0,0.08);
  margin-top: 0.19rem;
  transition: border 0.2s, color 0.2s, background 0.2s;
}

.banner-glass-btn:hover {
  color: #fff !important;
  background: linear-gradient(90deg, #ff9800 0%, #ff5722 100%) !important;
  border-color: #ff5722 !important;
}

@media (max-width: 1200px) {
  .banner-glass-content {
    max-width: 28vw;
    min-width: 100px;
    padding: 0.7rem 1.1rem 0.7rem 0.8rem;
  }
  .banner-title {
    font-size: 0.95rem;
  }
  .banner-desc {
    font-size: 0.85rem;
  }
  .banner-glass-btn {
    font-size: 0.85rem;
    padding: 0.13rem 0.7rem;
  }
}
@media (max-width: 767px) {
  .banner-glass-content {
    left: 1vw;
    bottom: 1vw;
    max-width: 40vw;
    min-width: 60px;
    padding: 0.35rem 0.55rem 0.35rem 0.4rem;
  }
  .banner-title {
    font-size: 0.81rem;
  }
  .banner-desc {
    font-size: 0.69rem;
  }
  .banner-glass-btn {
    font-size: 0.69rem;
    padding: 0.09rem 0.5rem;
  }
}

/* --- Banner Carousel Navigation --- */
.banner-carousel-nav {
  position: absolute;
  left: 50%;
  bottom: 1.2rem;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  gap: 1.1rem;
  align-items: center;
  pointer-events: none;
}

.banner-nav-btn {
  background: rgba(255,255,255,0.82);
  border: none;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.13);
  color: #ff9800;
  font-size: 1.5rem;
  cursor: pointer;
  transition: background 0.18s, color 0.18s, box-shadow 0.18s;
  pointer-events: auto;
}

.banner-nav-btn:hover, .banner-nav-btn:focus {
  background: linear-gradient(90deg, #ff9800 0%, #ff5722 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(255,152,0,0.18);
}

/* --- Banner Carousel Navigation Dots --- */
.banner-carousel-dots {
  position: absolute;
  left: 50%;
  bottom: 1.2rem;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  background: rgba(0,0,0,0.2);
  padding: 5px 12px;
  border-radius: 20px;
}

.banner-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e0e0e0;
  border: none;
  cursor: pointer;
  transition: background 0.18s;
  display: block;
}

.banner-dot.active {
  background: #ff9800;
  width: 12px;
  height: 12px;
}


</style>
